import React, { useEffect } from 'react';
import {
  Box,
  Flex,
  HStack,
  useDisclosure,
  useColorModeValue,
  useColorMode,
  Stack,
  IconButton,
  Container,
  Text,
  Image,
} from '@chakra-ui/react';
import { HamburgerIcon, CloseIcon, MoonIcon, SunIcon } from '@chakra-ui/icons';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSelector from './LanguageSelector';

interface NavItem {
  label: string;
  href: string;
}

const NavLink = ({
  children,
  href,
  onClick,
}: {
  children: React.ReactNode;
  href: string;
  onClick?: () => void;
}) => (
  <RouterLink to={href} onClick={onClick}>
    <Text
      _hover={{
        textDecoration: 'none',
      }}
    >
      {children}
    </Text>
  </RouterLink>
);

const Header: React.FC = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();
  const { colorMode, toggleColorMode } = useColorMode();
  const { t } = useTranslation();

  const NavItems: NavItem[] = [
    { label: t('nav.home'), href: '/' },
    { label: t('nav.solutions'), href: '/solutions' },
    { label: t('nav.products'), href: '/products' },
    { label: t('nav.demo'), href: '/demo' },
    { label: t('nav.contact'), href: '/contact' },
  ];

  useEffect(() => {
    onClose();
  }, [location.pathname, onClose]);

  const isActive = (href: string) => location.pathname === href;

  return (
    <Box
      bg={useColorModeValue('white', 'gray.800')}
      px={4}
      boxShadow='sm'
      position='sticky'
      top={0}
      zIndex={1000}
      borderBottom='1px solid'
      borderColor={useColorModeValue('gray.200', 'gray.700')}
    >
      <Container maxW='7xl'>
        <Flex h={16} alignItems={'center'} justifyContent={'space-between'}>
          <RouterLink to='/'>
            <Box>
              <Image
                src='/logo.svg'
                alt='PlayZura Logo'
                height='40px'
                width='auto'
                objectFit='contain'
              />
            </Box>
          </RouterLink>

          {/* Desktop Navigation */}
          <HStack
            spacing={8}
            alignItems={'center'}
            display={{ base: 'none', md: 'flex' }}
          >
            <HStack as={'nav'} spacing={4}>
              {NavItems.map(item => (
                <NavLink key={item.label} href={item.href}>
                  <Text
                    color={
                      isActive(item.href)
                        ? 'primary.500'
                        : useColorModeValue('gray.700', 'white')
                    }
                    fontWeight={isActive(item.href) ? '600' : '500'}
                    pb={1}
                    _hover={{
                      color: isActive(item.href)
                        ? 'primary.500'
                        : useColorModeValue('gray.800', 'gray.200'),
                    }}
                  >
                    {item.label}
                  </Text>
                </NavLink>
              ))}
            </HStack>
          </HStack>

          {/* Right side items */}
          <Flex alignItems={'center'} gap={4}>
            {/* Theme Switcher */}
            <IconButton
              aria-label='Toggle color mode'
              icon={colorMode === 'light' ? <MoonIcon /> : <SunIcon />}
              onClick={toggleColorMode}
              variant='ghost'
              size='sm'
              color={useColorModeValue('gray.700', 'gray.200')}
              _hover={{
                bg: useColorModeValue('gray.100', 'gray.700'),
              }}
            />
            <LanguageSelector />

            {/* Mobile menu button */}
            <IconButton
              size={'md'}
              icon={isOpen ? <CloseIcon /> : <HamburgerIcon />}
              aria-label={'Open Menu'}
              display={{ md: 'none' }}
              onClick={isOpen ? onClose : onOpen}
              color={useColorModeValue('gray.700', 'white')}
              _hover={{
                bg: useColorModeValue('gray.100', 'gray.700'),
              }}
            />
          </Flex>
        </Flex>

        {/* Mobile Navigation */}
        {isOpen ? (
          <Box pb={4} display={{ md: 'none' }}>
            <Stack as={'nav'} spacing={4}>
              {NavItems.map(item => (
                <NavLink key={item.label} href={item.href} onClick={onClose}>
                  <Text
                    color={
                      isActive(item.href)
                        ? 'primary.500'
                        : useColorModeValue('gray.700', 'white')
                    }
                    fontWeight={isActive(item.href) ? '600' : '500'}
                    py={2}
                    _hover={{
                      color: isActive(item.href)
                        ? 'primary.500'
                        : useColorModeValue('gray.800', 'gray.200'),
                    }}
                  >
                    {item.label}
                  </Text>
                </NavLink>
              ))}
            </Stack>
          </Box>
        ) : null}
      </Container>
    </Box>
  );
};

export default Header;
